// Opt-in, local-only measurements. Never retain station/session/user data.
export function summarizeSamples(values) {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const round = value => Math.round(value * 100) / 100
  return { samples: values.length, mean: round(values.reduce((a, b) => a + b, 0) / values.length), p95: round(sorted[Math.ceil(sorted.length * 0.95) - 1]), max: round(sorted[sorted.length - 1]) }
}

export class CoursePerformance {
  static fromSearch(search) {
    const params = new URLSearchParams(search)
    const mode = params.get('course-perf')
    if (!['baseline', 'optimized'].includes(mode)) return null
    const raw = params.get('course-count')
    const count = ['0', '16', '32', '64'].includes(raw) ? Number(raw) : null
    return new CoursePerformance(mode, count)
  }
  constructor(mode, count) {
    this.mode = mode
    this.count = count
    this.samples = {}
    this.refreshes = []
    this.reports = []
    this.restore = []
    this.sourceCount = 0
  }
  record(key, value) {
    if (!Number.isFinite(value) || value < 0) return
    const values = this.samples[key] || (this.samples[key] = [])
    if (values.length < 4096) values.push(value)
  }
  recordRefresh(kind, cpuMs, sourceCount) {
    if (this.renderedCount !== this.course?.state.count) {
      this.resetWindow()
      this.renderedCount = this.course?.state.count
    }
    this.sourceCount = sourceCount
    if (this.refreshes.length < 100) this.refreshes.push({ kind, cpuMs: Math.round(cpuMs * 100) / 100 })
  }
  wrap(target, key, metric) {
    const original = target[key]
    const perf = this
    function wrapped(...args) {
      const started = performance.now()
      try { return original.apply(this, args) }
      finally { perf.record(metric, performance.now() - started) }
    }
    target[key] = wrapped
    this.restore.push(() => { if (target[key] === wrapped) target[key] = original })
  }
  attach(world, course) {
    this.world = world
    this.course = course
    this.wrap(world.graphics, 'render', 'renderSubmitCpuMs')
    this.wrap(world.physics, 'postFixedUpdate', 'physicsStepCpuMs')
    // Measure actual frame intervals before World clamps delta to 1/30.
    const originalPre = world.preTick
    const originalPost = world.postTick
    const perf = this
    function pre() {
      const now = performance.now()
      const xr = !!world.xr.session
      if (xr !== perf.xr) { perf.resetWindow(); perf.xr = xr }
      if (perf.previous !== undefined) perf.record('frameIntervalMs', now - perf.previous)
      perf.previous = now
      perf.frameStart = now
      return originalPre.call(this)
    }
    function post() {
      originalPost.call(this)
      perf.record('frameCpuMs', performance.now() - perf.frameStart)
      const info = world.graphics.renderer.info
      perf.record('drawCalls', info.render.calls)
      perf.record('triangles', info.render.triangles)
      if (performance.now() - perf.windowStart >= 10000 && course.state.enabled && !document.hidden) perf.report()
    }
    world.preTick = pre
    world.postTick = post
    this.restore.push(() => {
      if (world.preTick === pre) world.preTick = originalPre
      if (world.postTick === post) world.postTick = originalPost
    })
    this.onVisibility = () => this.resetWindow()
    document.addEventListener('visibilitychange', this.onVisibility)
    this.resetWindow()
  }
  resetWindow() {
    this.samples = {}
    this.refreshes = []
    this.previous = undefined
    this.windowStart = performance.now()
  }
  report() {
    const renderer = this.world.graphics.renderer
    const metrics = Object.fromEntries(Object.entries(this.samples).map(([key, values]) => [key, summarizeSamples(values)]))
    const interval = metrics.frameIntervalMs?.mean
    const report = {
      mode: this.mode, requestedCount: this.count, sourceCount: this.sourceCount,
      renderedCount: this.course.state.count, xr: !!this.world.xr.session,
      xrVisibility: this.world.xr.session?.visibilityState || null,
      xrTargetHz: this.world.xr.session?.frameRate || null,
      // Callback cadence is not GPU time or proof of compositor-delivered FPS.
      callbackHz: interval ? Math.round(100000 / interval) / 100 : null,
      elapsedMs: Math.round(performance.now() - this.windowStart), metrics,
      refreshes: this.refreshes,
      resources: { ...renderer.info.memory, programs: renderer.info.programs?.length || 0 },
      jsHeapBytes: performance.memory?.usedJSHeapSize ?? null,
      stationColliderBodies: this.course.collisionBodies.length,
      stationButtons: this.course.worldButtons.length,
      stationPanelTextures: this.course.stationRenderer ? 1 : this.course.disposableTextures.length,
      stationRenderBatches: this.course.stationRenderer?.batches.length ?? null,
      highDetailStations: this.course.stationRenderer?.detail.filter(Boolean).length ?? null,
      gpuTimeMs: null, modelLoad: this.modelLoad || null,
    }
    this.reports.push(report)
    if (this.reports.length > 18) this.reports.shift()
    console.info('[course-perf]', JSON.stringify(report))
    this.resetWindow()
    return report
  }
  destroy() {
    for (const restore of this.restore.reverse()) restore()
    this.restore = []
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.reports = []
    this.world = null
    this.course = null
  }
}
